import React, { type FunctionComponent, useContext, useEffect, useState } from 'react'
import { Dialog } from '../../../../common/Dialog'
import { API_HOST, authenticatedFetchData } from '../../../../../lib'
import { logError } from '@tomra/datadog-browser-logging'
import { Diff } from './diff/Diff'
import { findObjectDifference } from './diff/diffHelpers'
import { hasCharityEditPermission } from '../../../../../services/permission.service'
import { AlertIcon } from '../../../../icons'
import { ApprovePendingChangesButton } from './ApprovePendingChangesButton'
import { RejectPendingChangesButton } from './RejectPendingChangesButton'
import { isBefore } from 'date-fns'
import { CharityReviewContext } from '../../../../contexts'
import { AttachmentList } from '../../charities-review/charity-review/attachments/AttachmentList'
import { fetchPendingAttachments } from '../../../../../services/charity.service'

const restrictedFields = [
  'id',
  'administratorUserId',
  'administratorEmail',
  'createdAt',
  'createdBy',
  'updatedAt',
  'updatedBy',
  'status',
  'campaignId',
  'charityId',
  'barcode'
]

type Props = {
  charityId: string
  campaignId?: string
  changesToReview: CharityType | CharityCampaignType
  onComplete: () => void
  onCancel: () => void
}

export const ReviewPendingChanges: FunctionComponent<Props> = ({
  charityId,
  campaignId,
  changesToReview,
  onComplete,
  onCancel
}) => {
  const [status, setStatus] = useState<RequestStatusType | 'approve' | 'reject'>('idle')
  const [pendingAttachmentStatus, setPendingAttachmentStatus] = useState<RequestStatusType>('idle')
  const [originalObject, setOriginalObject] = useState<CharityType | CharityCampaignType | null>()
  const [diff, setDiff] = useState<DiffObjectType | null>(null)
  const { pendingAttachments, setPendingAttachments } = useContext(CharityReviewContext)

  useEffect(() => {
    setStatus('pending')

    const originalUrl = campaignId
      ? `${API_HOST}/charities/admin/v1/charities/${charityId}/campaign/${campaignId}`
      : `${API_HOST}/charities/admin/v1/charities/${charityId}`

    const fetchOriginal = authenticatedFetchData(originalUrl)
    fetchOriginal
      .run()
      .then(setOriginalObject)
      .catch((error: any) => {
        if (error.name !== 'AbortError') {
          logError(new Error('Failed to load original object'), error)
          setStatus('failure')
        }
      })

    return () => {
      fetchOriginal.abort()
    }
  }, [changesToReview])

  useEffect(() => {
    async function getAllAttachments() {
      try {
        setPendingAttachmentStatus('pending')
        const attachments = await fetchPendingAttachments(charityId)
        setPendingAttachments(attachments)
        setPendingAttachmentStatus('idle')
      } catch (error: unknown) {
        setPendingAttachmentStatus('failure')

        if ((error as Error)?.name !== 'AbortError') {
          logError(new Error('Failed to load charity attachments'), error as Error)
        }
      }
    }

    getAllAttachments()
  }, [charityId])

  useEffect(() => {
    if (!originalObject) {
      return
    }

    const removeRestrictedFields = (object: CharityType | CharityCampaignType) => {
      const isCampaign = Object.hasOwn(object, 'campaignId')

      const fieldsToRemove = isCampaign ? [...restrictedFields, 'logoUrl'] : restrictedFields

      const propsToCompare = Object.keys(object).filter((field: string) => fieldsToRemove.indexOf(field) === -1)

      return Object.keys(object).reduce(
        (acc, key) => {
          if (propsToCompare.indexOf(key) === -1) {
            return acc
          }

          return { ...acc, [key]: object[key as keyof (CharityType | CharityCampaignType)] }
        },
        {} as Partial<CharityType | CharityCampaignType>
      )
    }

    setDiff(findObjectDifference(removeRestrictedFields(originalObject), removeRestrictedFields(changesToReview)))
  }, [changesToReview, originalObject])

  const changesIncludeEndDate = diff ? Object.keys(diff).indexOf('endTime') !== -1 : false
  const newEndDateHasPassed = changesIncludeEndDate ? isBefore(new Date(diff?.endTime.newValue), new Date()) : false

  return (
    <>
      <Dialog ariaLabel="Review changes" onClose={onCancel}>
        <div className="card min-w-screen-lg max-w-screen-lg max-h-[90vh] overflow-hidden">
          <div className="relative px-lg min-w-screen-md max-w-screen-xl overflow-scroll" style={{ maxHeight: '80vh' }}>
            <h1 className="sticky py-lg z-[1] top-0 bg-white text-xl mb-md">Review changes</h1>

            {!hasCharityEditPermission() && <div className="alert alert-warning">You only have read-permissions</div>}

            {newEndDateHasPassed && (
              <div className="alert alert-danger">The new end date has passed. You must reject the changes.</div>
            )}

            {diff ? (
              <>
                <Diff diff={diff} />

                {pendingAttachmentStatus === 'pending' ? (
                  <div className="loadingSpinner" />
                ) : (
                  pendingAttachments?.length > 0 && (
                    <div className="my-lg">
                      <h2 className="text-md mb-md text-storm">New attachments</h2>
                      <AttachmentList charityId={charityId} attachments={pendingAttachments} isStaged={true} />
                    </div>
                  )
                )}
              </>
            ) : status === 'failure' ? (
              <div className="h-10 flex flex-col items-center justify-center space-y-md text-red-dark">
                <AlertIcon width="2rem" height="2rem" />
                <div>Something went wrong</div>
              </div>
            ) : (
              <div className="h-10 flex items-center justify-center">
                <div className="loadingSpinner" />
              </div>
            )}
          </div>

          {hasCharityEditPermission() && (
            <div className="flex space-x-md justify-end p-lg bg-grey-light">
              <RejectPendingChangesButton charityId={charityId} campaignId={campaignId} onClose={onComplete} />
              {!newEndDateHasPassed && (
                <ApprovePendingChangesButton charityId={charityId} campaignId={campaignId} onClose={onComplete} />
              )}
            </div>
          )}
        </div>
      </Dialog>
    </>
  )
}
